// The switch between the flat 2D look and the 3D look, plus the building list the
// 3D scene draws. Kept in its own module with no imports so render.ts and the modes
// can both use it without a cycle.
import type { Snapshot, IncomingStrike, ClaimRect } from '@thrash/sim';
import type { DeathFx, DrawableBuilding, LiveShot, SmokePuff } from './render.ts';

const params = new URLSearchParams(location.search);
const KEY = 'thrash.3d';
// 3D is the default; ?3d=0 or a saved 0 gives the flat 2D look, ?3d=1 forces 3D
let on = true;
try {
  const flag = params.get('3d');
  on = flag === '1' ? true : flag === '0' ? false : localStorage.getItem(KEY) !== '0';
} catch { /* storage unavailable: the default stands */ }

export function is3D(): boolean {
  return on;
}

let buildings: readonly DrawableBuilding[] = [];
let version = 0;

/** The buildings the 3D scene should show; called whenever the building layer would be redrawn. */
export function setBuildings3D(list: readonly DrawableBuilding[]): void {
  buildings = list;
  version++;
}

export function buildings3D(): readonly DrawableBuilding[] {
  return buildings;
}

/** Bumps every time the list changes, so the scene knows to rebuild its meshes. */
export function buildingsVersion(): number {
  return version;
}

// ---- units and effects ---------------------------------------------------------------
// The raid hands these over each frame in place of drawing them with Pixi; the 3D scene
// reads them when it renders. Positions are sim units, as the snapshots give them.

export interface Units3D { prev: Snapshot; next: Snapshot; t: number; selected: ReadonlySet<number> }
let units: Units3D | null = null;
let shots: readonly LiveShot[] = [];
let deaths: readonly DeathFx[] = [];
let smoke: readonly SmokePuff[] = [];

export function setUnits3D(u: Units3D | null): void { units = u; }
export function units3D(): Units3D | null { return units; }
export function setShots3D(s: readonly LiveShot[]): void { shots = s; }
export function shots3D(): readonly LiveShot[] { return shots; }
export function setDeaths3D(d: readonly DeathFx[]): void { deaths = d; }
export function deaths3D(): readonly DeathFx[] { return deaths; }
export function setSmoke3D(s: readonly SmokePuff[]): void { smoke = s; }
export function smoke3D(): readonly SmokePuff[] { return smoke; }

/** Drops the raid's units and effects, so none linger on the village. */
export function clearFx3D(): void {
  units = null; shots = []; deaths = []; smoke = []; incoming = [];
}

/** Nukes and EMPs called in and counting down, as the sim gives them (tile, ticks left). */
export interface Incoming3D { kind: IncomingStrike['kind']; x: number; y: number; left: number }
let incoming: readonly Incoming3D[] = [];
export function setIncoming3D(list: readonly Incoming3D[]): void { incoming = list; }
export function incoming3D(): readonly Incoming3D[] { return incoming; }

/** The village's claimed land, to shade beyond and outline; null in a fight, where nothing is built. */
let claim: ClaimRect | null = null;
export function setClaim3D(c: ClaimRect | null): void { claim = c; }
export function claim3D(): ClaimRect | null { return claim; }

// The animated building parts (firing, disabled, damaged). Set every frame by the raid,
// so a turret that starts firing shows it at once without rebuilding the buildings.
let buildingFx: readonly DrawableBuilding[] = [];
export function setBuildingFx3D(list: readonly DrawableBuilding[]): void { buildingFx = list; }
export function buildingFx3D(): readonly DrawableBuilding[] { return buildingFx; }

// ---- the road ----------------------------------------------------------------------------
// The map in play, for the 3D scene to lay its road and keep the grass off it; null on the menu.
import type { TdMap } from '@thrash/sim';
let road: TdMap | null = null;
export function setRoad3D(m: TdMap | null): void { road = m; }
export function road3D(): TdMap | null { return road; }
